import React from "react"
import { CalendarSettingsIdInput } from "../../../calendar-settings/api/calendar-settings-id"
import { useCalendarSettings } from "../../../hooks/use-calendar-settings"
import { useContextHolidays } from "../../../hooks/use-context-holidays"
import { useGetTasksQuery } from "../../../tasks/api"
import { TasksViewOptions } from "../../../tasks/components/TasksViewSelect"
import { AppMainContextType, AppSubContextType } from "../../../types/app-context"
import { makeTaskListQuery } from "../utils/task-list-query"

const queryOptions = { refetchOnMountOrArgChange: true } as const

export const useTaskListRouteData = ({ context, mainContext, showTaskList, view }: UseTaskListRouteDataProps) => {
  const query = makeTaskListQuery(context, view)
  const { data: tasks = [], isLoading, isError, status } = useGetTasksQuery(query, queryOptions)

  const projectCalendarSettingsIds = React.useMemo<CalendarSettingsIdInput[]>(() => {
    const projectIds = uniq(tasks.map((task) => task.projectId))
    return projectIds.map((id) => ({ type: "project", id }))
  }, [tasks])
  const shouldSkipProjectCalendarSettingsIds = !showTaskList || projectCalendarSettingsIds.length === 0
  const {
    calendarSettingsById,
    firstDayOfWeek,
    weekendDays,
    settingsKey,
    isLoading: isLoadingProjectCalendarSettings,
    isError: isErrorProjectCalendarSettings,
    queryErrors: calendarSettingsQueryErrors,
  } = useCalendarSettings({
    mainContext,
    calendarSettingsIds: projectCalendarSettingsIds,
    skipCalendarSettingsIds: shouldSkipProjectCalendarSettingsIds,
    skipAll: !mainContext || status !== "fulfilled",
  })
  const {
    holidays,
    isLoading: isLoadingHolidays,
    isError: isErrorHolidays,
    queryErrors: holidaysQueryErrors,
  } = useContextHolidays(mainContext)

  return {
    calendarSettingsById,
    calendarSettingsQueryErrors,
    firstDayOfWeek,
    holidays,
    holidaysQueryErrors,
    isError,
    isErrorHolidays,
    isErrorProjectCalendarSettings,
    isLoading,
    isLoadingHolidays,
    isLoadingProjectCalendarSettings,
    query,
    settingsKey,
    status,
    tasks,
    weekendDays,
  }
}

const uniq = <T>(array: T[]): T[] => {
  return Array.from(new Set(array))
}

type UseTaskListRouteDataProps = {
  context: AppMainContextType | AppSubContextType | null
  mainContext: AppMainContextType | null
  showTaskList: boolean
  view: TasksViewOptions
}
